/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Typed reason when the run failed. rate_limited and okta_unreachable are retried before being recorded; superseded means a newer run applied first; discarded means the connection stopped being verified during the run; interrupted means the worker died.
 */
export const ErrorT = {
  RateLimited: "rate_limited",
  CredentialRejected: "credential_rejected",
  OktaUnreachable: "okta_unreachable",
  ClientUnavailable: "client_unavailable",
  Superseded: "superseded",
  Discarded: "discarded",
  Interrupted: "interrupted",
} as const;
/**
 * Typed reason when the run failed. rate_limited and okta_unreachable are retried before being recorded; superseded means a newer run applied first; discarded means the connection stopped being verified during the run; interrupted means the worker died.
 */
export type ErrorT = OpenEnum<typeof ErrorT>;

/**
 * running, succeeded, or failed.
 */
export const IdentityProviderConnectionReconcileRunStatus = {
  Running: "running",
  Succeeded: "succeeded",
  Failed: "failed",
} as const;
/**
 * running, succeeded, or failed.
 */
export type IdentityProviderConnectionReconcileRunStatus = OpenEnum<
  typeof IdentityProviderConnectionReconcileRunStatus
>;

/**
 * One applications snapshot run and the changes it applied.
 */
export type IdentityProviderConnectionReconcileRun = {
  applicationsAdded: number;
  applicationsRemoved: number;
  /**
   * Applications in the snapshot after skipping Okta-internal ones.
   */
  applicationsSeen: number;
  assignmentsAdded: number;
  assignmentsRemoved: number;
  /**
   * Typed reason when the run failed. rate_limited and okta_unreachable are retried before being recorded; superseded means a newer run applied first; discarded means the connection stopped being verified during the run; interrupted means the worker died.
   */
  error?: ErrorT | undefined;
  /**
   * Omitted while running.
   */
  finishedAt?: Date | undefined;
  /**
   * Run ID.
   */
  id: string;
  /**
   * Okta-internal application ids left out of the snapshot.
   */
  skippedAppIds: Array<string>;
  startedAt: Date;
  /**
   * running, succeeded, or failed.
   */
  status: IdentityProviderConnectionReconcileRunStatus;
  /**
   * Whether a listing hit the page or application cap; nothing missing from a truncated listing is removed.
   */
  truncated: boolean;
};

/** @internal */
export const ErrorT$inboundSchema: z.ZodMiniType<ErrorT, unknown> = openEnums
  .inboundSchema(ErrorT);

/** @internal */
export const IdentityProviderConnectionReconcileRunStatus$inboundSchema:
  z.ZodMiniType<IdentityProviderConnectionReconcileRunStatus, unknown> =
    openEnums.inboundSchema(IdentityProviderConnectionReconcileRunStatus);

/** @internal */
export const IdentityProviderConnectionReconcileRun$inboundSchema:
  z.ZodMiniType<IdentityProviderConnectionReconcileRun, unknown> = z.pipe(
    z.object({
      applications_added: z.int(),
      applications_removed: z.int(),
      applications_seen: z.int(),
      assignments_added: z.int(),
      assignments_removed: z.int(),
      error: z.optional(ErrorT$inboundSchema),
      finished_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      id: z.string(),
      skipped_app_ids: z.array(z.string()),
      started_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      status: IdentityProviderConnectionReconcileRunStatus$inboundSchema,
      truncated: z.boolean(),
    }),
    z.transform((v) => {
      return remap$(v, {
        "applications_added": "applicationsAdded",
        "applications_removed": "applicationsRemoved",
        "applications_seen": "applicationsSeen",
        "assignments_added": "assignmentsAdded",
        "assignments_removed": "assignmentsRemoved",
        "finished_at": "finishedAt",
        "skipped_app_ids": "skippedAppIds",
        "started_at": "startedAt",
      });
    }),
  );

export function identityProviderConnectionReconcileRunFromJSON(
  jsonString: string,
): SafeParseResult<IdentityProviderConnectionReconcileRun, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) =>
      IdentityProviderConnectionReconcileRun$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'IdentityProviderConnectionReconcileRun' from JSON`,
  );
}
